import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { api } from "./apiClient";

export const useInstances = () =>
  useQuery({
    queryKey: ["instances"],
    queryFn: () => api.listInstances(),
    refetchInterval: 5000,
  });

export const useWorkspaces = () =>
  useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
    refetchInterval: 5000,
  });

export const useMarkViewed = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (instanceId: string) => api.markInstanceViewed({ instanceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
    },
  });
};

export const useRemoveContainer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => api.removeContainer({ workspaceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

export const useMarkInstanceDone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) => api.markInstanceDone({ id, done }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
      queryClient.invalidateQueries({ queryKey: ["instance"] });
    },
  });
};

export const useRenameWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.renameWorkspace({ id, name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({ queryKey: ["workspace"] });
    },
  });
};

export const useRemoveContainerWithConfirm = (workspaceId: string | null | undefined) => {
  const router = useRouter();
  const mutation = useRemoveContainer();

  const execute = useCallback(() => {
    if (!workspaceId || mutation.isPending) return;

    if (!confirm("Remove this container? The container and worktree will be removed. History will be preserved.")) return;

    mutation.mutate(workspaceId, {
      onSuccess: () => router.push("/"),
      onError: (err) => {
        console.error("Failed to remove container:", err);
        alert("Failed to remove container");
      },
    });
  }, [workspaceId, mutation, router]);

  return { execute, isPending: mutation.isPending };
};
